// src/pages/paidProject/Annexure 1/TermLoanRepayment.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function TermLoanRepayment() {
  const { projectId } = useParams();

  // ─── State ─────────────────────────────────────────────────
  const [interestRate, setInterestRate] = useState("");
  const [loanPeriod, setLoanPeriod] = useState("");
  const [moratorium, setMoratorium] = useState("");
  const [pStartMonth, setPStartMonth] = useState("");
  const [startMonth, setStartMonth] = useState("");
  const [disbursements, setDisbursements] = useState([]);
  const [schedule, setSchedule] = useState([]);

  // ─── Helpers ────────────────────────────────────────────────
  const formatAmt = (n) =>
    isNaN(n)
      ? "-"
      : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  const generateMonthLabels = (startStr, length) => {
    if (!startStr.includes("-")) return [];
    const [startM, startY] = startStr.split("-").map(Number);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    let m = startM - 1,
      y = startY;
    return Array.from({ length }, () => {
      const label = `${months[m]}-${y}`;
      m = (m + 1) % 12;
      if (m === 0) y++;
      return label;
    });
  };

  // ─── Load inputs & existing schedule ────────────────────────
  useEffect(() => {
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        const t = data.termLoanInput || {};
        setInterestRate(t.interestRate || "");
        setLoanPeriod(t.loanPeriod || "");
        setMoratorium(t.moratorium || "");
        setPStartMonth(t.pStartMonth || "");
        setStartMonth(t.startMonth || "");
        setDisbursements(Array.isArray(t.disbursements) ? t.disbursements : []);
        if (Array.isArray(t.emiSchedule) && t.emiSchedule.length) {
          console.log("📥 Loaded existing emiSchedule:", t.emiSchedule);
          setSchedule(t.emiSchedule);
        }
      })
      .catch((err) => console.error("❌ Failed to load term loan inputs", err));
  }, [projectId]);

  // ─── Generate schedule ──────────────────────────────────────
  const generateSchedule = (ir, period, mor, disb, pStart) => {
    const r = ir / 100 / 12;
    const repaymentMonths = period - mor;
    const loanAmount = disb.reduce((s, v) => s + v, 0);
    const emi =
      (loanAmount * r * Math.pow(1 + r, repaymentMonths)) /
      (Math.pow(1 + r, repaymentMonths) - 1);

    const result = [];
    for (let i = 0, openingBalance = 0; i < period; i++) {
      const addition = disb[i] || 0;
      let balance = openingBalance + addition;
      const interest = balance * r;
      let principal = 0,
        totalPayment = 0;

      if (i + 1 > mor) {
        totalPayment = emi;
        principal = emi - interest;
        balance -= principal;
      } else {
        totalPayment = interest;
      }

      result.push({
        month: i + 1,
        openingBalance,
        addition,
        interest,
        principal,
        totalPayment,
        closingBalance: balance > 0 ? balance : 0,
      });
      openingBalance = balance;
    }

    console.log("🔄 Generated EMI schedule:", result);
    setSchedule(result);
  };

  // ─── Auto‐generate if inputs present ────────────────────────
  useEffect(() => {
    if (
      interestRate > 0 &&
      loanPeriod > 0 &&
      moratorium >= 0 &&
      disbursements.length &&
      pStartMonth.includes("-")
    ) {
      generateSchedule(
        parseFloat(interestRate),
        parseInt(loanPeriod, 10),
        parseInt(moratorium, 10),
        disbursements.map((d) => parseFloat(d) || 0),
        pStartMonth
      );
    }
  }, [interestRate, loanPeriod, moratorium, disbursements, pStartMonth]);

  // ─── Persist schedule whenever it changes ───────────────────
  useEffect(() => {
    if (!schedule.length) return;

    const payload = {
      termLoanInput: {
        interestRate: parseFloat(interestRate),
        loanPeriod: parseInt(loanPeriod, 10),
        moratorium: parseInt(moratorium, 10),
        pStartMonth,
        startMonth,
        disbursements,
        emiSchedule: schedule,
      },
    };
    API.put(`/user/project-inputs/${projectId}`, payload)
      .then(() => {
        console.log("✅ EMI schedule saved");
      })
      .catch((err) => console.error("❌ Failed to save EMI schedule", err));
  }, [
    schedule,
    projectId,
    interestRate,
    loanPeriod,
    moratorium,
    pStartMonth,
    startMonth,
    disbursements,
  ]);

  // ─── Month labels & totals ───────────────────────────────────
  const monthLabels = generateMonthLabels(
    pStartMonth,
    parseInt(loanPeriod) || 0
  );
  const totals = schedule.reduce(
    (acc, row) => {
      acc.addition += row.addition;
      acc.interest += row.interest;
      acc.principal += row.principal;
      acc.totalPayment += row.totalPayment;
      return acc;
    },
    { addition: 0, interest: 0, principal: 0, totalPayment: 0 }
  );

  // ─── Render with Tailwind ───────────────────────────────────
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Term Loan Repayment Schedule</h1>

      <div className="bg-white border rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium">
              Interest Rate (%)
            </label>
            <input
              readOnly
              value={interestRate}
              className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">
              Loan Period (Months)
            </label>
            <input
              readOnly
              value={loanPeriod}
              className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">
              Moratorium (Months)
            </label>
            <input
              readOnly
              value={moratorium}
              className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">
              Start Month (MM-YYYY)
            </label>
            <input
              readOnly
              value={pStartMonth}
              className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">
              Commercial Month (MM-YYYY)
            </label>
            <input
              readOnly
              value={startMonth}
              className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mt-4">
          {disbursements.map((val, idx) => (
            <div key={idx}>
              <label className="block text-sm font-medium">
                Disbursal {idx + 1}
              </label>
              <input
                readOnly
                value={formatAmt(val)}
                className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1"
              />
            </div>
          ))}
        </div>
      </div>

      {schedule.length === 0 ? (
        <p className="text-red-600">No repayment schedule available yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 border">Month</th>
                <th className="px-4 py-2 border">Opening Balance</th>
                <th className="px-4 py-2 border">Addition</th>
                <th className="px-4 py-2 border">Interest</th>
                <th className="px-4 py-2 border">Principal</th>
                <th className="px-4 py-2 border">Total Payment</th>
                <th className="px-4 py-2 border">Closing Balance</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((row, idx) => (
                <tr key={idx} className={idx % 2 ? "bg-gray-50" : "bg-white"}>
                  <td className="px-4 py-2 border">{monthLabels[idx]}</td>
                  <td className="px-4 py-2 border">
                    {formatAmt(row.openingBalance)}
                  </td>
                  <td className="px-4 py-2 border">
                    {formatAmt(row.addition)}
                  </td>
                  <td className="px-4 py-2 border">
                    {formatAmt(row.interest)}
                  </td>
                  <td className="px-4 py-2 border">
                    {formatAmt(row.principal)}
                  </td>
                  <td className="px-4 py-2 border">
                    {formatAmt(row.totalPayment)}
                  </td>
                  <td className="px-4 py-2 border">
                    {formatAmt(row.closingBalance)}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-200 font-semibold">
                <td className="px-4 py-2 border">Grand Total</td>
                <td className="px-4 py-2 border"></td>
                <td className="px-4 py-2 border">
                  {formatAmt(totals.addition)}
                </td>
                <td className="px-4 py-2 border">
                  {formatAmt(totals.interest)}
                </td>
                <td className="px-4 py-2 border">
                  {formatAmt(totals.principal)}
                </td>
                <td className="px-4 py-2 border">
                  {formatAmt(totals.totalPayment)}
                </td>
                <td className="px-4 py-2 border"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
